/**
 * Agent Integration Types
 * Types for AI agent-assisted import processing
 */

export const AgentSessionType = {
  Assets: 'Assets',
  Transactions: 'Transactions',
} as const;
export type AgentSessionType =
  (typeof AgentSessionType)[keyof typeof AgentSessionType];

export const AgentSessionStatus = {
  Pending: 'Pending',
  FetchingData: 'FetchingData',
  Processing: 'Processing',
  Completed: 'Completed',
  Failed: 'Failed',
  Cancelled: 'Cancelled',
  TimedOut: 'TimedOut',
} as const;
export type AgentSessionStatus =
  (typeof AgentSessionStatus)[keyof typeof AgentSessionStatus];

export interface AgentImportSession {
  sessionId: string;
  sessionType: AgentSessionType;
  status: AgentSessionStatus;
  totalItems: number;
  processedItems: number;
  createdAt: string;
  startedAt?: string;
  completedAt?: string;
  errorMessage?: string;
}

export interface AgentSuggestion {
  fieldName: string;
  originalValue?: string;
  suggestedValue: string;
  confidence: number;
  reason?: string;
}

export interface ProcessedAsset {
  primaryKey: number;
  assetCode: string;
  description: string;
  category?: string;
  location?: string;
  suggestions: AgentSuggestion[];
}

export interface ProcessedTransaction {
  transactionId: string;
  assetCode?: string;
  description?: string;
  category?: string;
  suggestions: AgentSuggestion[];
}

export interface FetchAssetsWithAgentResponse {
  assets: import('./asset-import.types').AccountingAsset[];
  agentSessionId?: string;
  agentStatus?: AgentSessionStatus;
}

export interface FetchTransactionsWithAgentResponse {
  transactions: import('./asset-import.types').Transaction[];
  totalCount: number;
  eligibleCount: number;
  alreadyUsedCount: number;
  excludedCount: number;
  agentSessionId?: string;
  agentStatus?: AgentSessionStatus;
}

export interface AgentProcessedDataResponse {
  sessionId: string;
  status: AgentSessionStatus;
  processedAssets?: ProcessedAsset[];
  processedTransactions?: ProcessedTransaction[];
}

// Numeric status values from backend (C# enum)
const NumericAgentStatus = {
  Pending: 0,
  FetchingData: 1,
  Processing: 2,
  Completed: 3,
  Failed: 4,
  Cancelled: 5,
  TimedOut: 6,
} as const;

export function isAgentProcessingComplete(
  status: AgentSessionStatus | number
): boolean {
  // Handle both string (frontend) and numeric (backend) status values
  return (
    status === AgentSessionStatus.Completed ||
    status === AgentSessionStatus.Failed ||
    status === AgentSessionStatus.Cancelled ||
    status === AgentSessionStatus.TimedOut ||
    status === NumericAgentStatus.Completed ||
    status === NumericAgentStatus.Failed ||
    status === NumericAgentStatus.Cancelled ||
    status === NumericAgentStatus.TimedOut
  );
}

export function isAgentProcessingInProgress(
  status: AgentSessionStatus | number
): boolean {
  // Handle both string (frontend) and numeric (backend) status values
  return (
    status === AgentSessionStatus.Pending ||
    status === AgentSessionStatus.FetchingData ||
    status === AgentSessionStatus.Processing ||
    status === NumericAgentStatus.Pending ||
    status === NumericAgentStatus.FetchingData ||
    status === NumericAgentStatus.Processing
  );
}

export function isAgentProcessingSuccessful(
  status: AgentSessionStatus | number
): boolean {
  return status === AgentSessionStatus.Completed || status === NumericAgentStatus.Completed;
}
